import type { DivIcon, LatLng, Map, Marker, MarkerOptions, TileLayerOptions , LatLngBounds, LatLngBoundsLiteral} from 'leaflet'
import { control, divIcon, latLng, marker, tileLayer } from 'leaflet'
import type MapMarkerEventData from '~/types/search/MapMarkerEventData'
import type PortalEnvelope from '~/types/search/PortalEnvelope'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'

class SearchResultsMap {
  private markers: Marker[] = []
  private bounds: LatLngBounds
  private envelope: PortalEnvelope // Store envelope for fallback
  private zIndex = 150

  private map: Map
  private tileLayerOptions: TileLayerOptions = {
    minZoom: 0,
    maxZoom: 18,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
  }

  public isAnimatingZoom = true
  public limits = true
  private zoomControl

  private markerEventsHandler: (eventName: string, data: MapMarkerEventData) => void

  private activeMarker: Marker | null = null // TODO: Where is this set/used?

  constructor (
    map: Map,
    envelope: PortalEnvelope,
    markerEventsHandler: (eventName: string, data: MapMarkerEventData) => void
  ) {
    this.markerEventsHandler = markerEventsHandler
    this.envelope = envelope // Save envelope for later use

    // const center = reactive([envelope.n, envelope.e])
    // const bounds: LatLngBounds = latLngBounds(
    //   latLng(envelope.s, envelope.w),
    //   latLng(envelope.n, envelope.e)
    // )
    const bounds = [
      { lat: envelope.s, lng: envelope.w },
      { lat: envelope.n, lng: envelope.e }
    ]
    map.fitBounds(bounds)

    this.map = map

    tileLayer('https://tiles.travanto.de/{z}/{x}/{y}.png', this.tileLayerOptions).addTo(this.map)
    control.attribution({ position: 'bottomright', prefix: false }).addTo(this.map)
    if (!(window.innerWidth <= 768)) {
      this.zoomControl = control.zoom({ position: 'bottomright' }).addTo(this.map)
    }
    this.bounds = this.map.getBounds()
  }

  public hideZoomControl (): void {
    if (this.zoomControl) {
      this.map.removeControl(this.zoomControl)
      this.zoomControl = null
    }
  }

  public showZoomControl (): void {
    if (!this.zoomControl && !(window.innerWidth <= 768)) {
      this.zoomControl = control.zoom({ position: 'bottomright' }).addTo(this.map)
    }
  }

  public clearMarkers (): void {
    // Guard: Check if map still exists
    if (!this.map) {
      return
    }

    const mapStore = useSearchResultsMapStore()

    const filteredMarkers = this.markers.filter((markerObject) => {
      const itemNr = markerObject.options?.itemNr
      return itemNr === mapStore.lastOpenObject
    })

    for (const markerObject of this.markers) {
      if (!filteredMarkers.includes(markerObject)) {
        this.map.removeLayer(markerObject)
      }
    }

    this.markers = filteredMarkers

    // Increment generation counter on the map container so tests can detect
    // that markers were cleared and re-rendered even when the texts are identical.
    // (THIS IS ONLY USED FOR THE PLAYWRIGHT TESTS)
    const container = this.map.getContainer()
    const prev = parseInt(container.getAttribute('data-map-generation') ?? '0')
    container.setAttribute('data-map-generation', String(prev + 1))
  }

  public toggleMarkerClassList (itemNr: number, classList: string, toggle: boolean): void {
    const markerObject = this.markers.find(marker => marker.options.itemNr === itemNr)
    if (!markerObject) {
      return
    }
    markerObject.getElement()?.classList.toggle(classList, toggle)
  }

  public openPopup (object: number): void {
    const marker = this.markers.find(marker => marker.options.itemNr === object)
    if (!marker) {
      return
    }
    marker.openPopup()
  }

  public addMarker (
    index: number,
    coordinates: string,
    itemNr: number,
    text: string,
    popupComponentElement: any,
    hasLastMinute: boolean = false,
    hasDiscount: boolean = false
    // objectNr: number
    // isObjectInLastViewedObjects = false
  ): void {
    // Guard: Check if map still exists
    if (!this.map) {
      return
    }

    const coordinatesArray = coordinates.split(',')
    const latLngObject: LatLng = latLng(
      Number(coordinatesArray[0]),
      Number(coordinatesArray[1])
    )
    const className = (hasLastMinute || hasDiscount)
      ? 'leaflet-custom-search-results-map-div-icon leaflet-marker-icon-lm'
      : 'leaflet-custom-search-results-map-div-icon'
    const normalDivIcon: DivIcon = divIcon({
      className,
      html: text,
      iconSize: [60, 20]
    })

    const normalMarkerOptions: MarkerOptions = {
      icon: normalDivIcon,
      riseOnHover: true,
      riseOffset: this.zIndex,
      zIndex: this.zIndex - 1 - index,
      itemNr,
      text
    }
    const markerObject: Marker = marker(latLngObject, normalMarkerOptions)

    markerObject.setZIndexOffset(this.zIndex - 1 - index)
    markerObject.addTo(this.map)
    markerObject.bindPopup(popupComponentElement, {
      autoPan: true,
      keepInView: true
    })
    const mapStore = useSearchResultsMapStore()

    this.bounds.extend(latLngObject)
    this.markers.push(markerObject)

    // markerObject.on('mouseover', () => {
    //   markerObject.setZIndexOffset(this.zIndex)
    // })
    // markerObject.on('mouseout', () => {
    //   markerObject.setZIndexOffset(this.zIndex - 1 - index)
    // })
    markerObject.on('mousedown', () => {
      mapStore.moveMapSearch = false
    })
    markerObject.on('popupopen', function () {
      mapStore.lastOpenObject = itemNr
    })

    markerObject.on('popupclose', function () {
      mapStore.lastOpenObject = null
    })

    markerObject.on('click', () => {
      mapStore.moveMapSearch = false
      if (this.activeMarker) {
        this.map.removeLayer(this.activeMarker)
      }
      const { $gtmModules } = useNuxtApp()
      $gtmModules.mapsModule.sendMapVacationRentalClickEvent()
    })

    markerObject.on('mouseover', () => {
      mapStore.hoveredObject = itemNr
    })

    markerObject.on('mouseout', () => {
      mapStore.hoveredObject = null
    })
  }

  public zoomMap (limits): void {
    // Guard: Check if map still exists and is in DOM
    if (!this.map || !this.map.getContainer()) {
      return
    }

    // Guard: Check if map container is connected to DOM
    const container = this.map.getContainer()
    if (!container.isConnected) {
      return
    }

    // Case 1: limits is a valid coordinate string (e.g., "lat1;lng1;lat2;lng2")
    if (limits && typeof limits === 'string' && limits.trim() !== '' && isNaN(limits)) {
      const [lat1, lng1, lat2, lng2] = limits.split(';').map(parseFloat)
      const bounds: LatLngBoundsLiteral = [
        [lat1, lng1],
        [lat2, lng2]
      ]
      this.map.flyToBounds(bounds).on('zoomend', () => {
        this.isAnimatingZoom = false
      })
      return
    }

    // Case 2: limits is empty/null/undefined -> zoom to markers or fallback to envelope
    if (this.markers.length === 0) {
      // No markers available -> fallback to envelope
      if (this.envelope) {
        const envelopeBounds = [
          { lat: this.envelope.s, lng: this.envelope.w },
          { lat: this.envelope.n, lng: this.envelope.e }
        ]
        this.map.flyToBounds(envelopeBounds, { duration: 1 }).on('zoomend', () => {
          this.isAnimatingZoom = false
        })
      }
      return
    }

    // Case 3: No valid limits, but markers exist -> zoom to markers
    const latLngs: LatLng[] = this.markers.map(marker => marker.getLatLng())
    const bounds: LatLngBoundsLiteral = latLngs.map(latLng => [latLng.lat, latLng.lng])

    this.map.flyToBounds(bounds, { duration: 1 }).on('zoomend', () => {
      this.isAnimatingZoom = false
    })
  }

  public destroy (): void {
    // Clear all markers
    this.markers.forEach((marker) => {
      if (this.map) {
        this.map.removeLayer(marker)
      }
    })
    this.markers = []

    // Note: We don't remove the map itself since we want to hide, not destroy
  }
}

export default SearchResultsMap
